import { useState, useEffect, useRef } from "react";
import { db, auth } from "./firebase";
import { collection, onSnapshot, query, orderBy, limit, doc, addDoc, updateDoc, getDocs, where, serverTimestamp, increment } from "firebase/firestore";

const fmtTime = (ts) => {
  if (!ts) return "";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 86400) return d.toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit" });
    return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" });
  } catch { return ""; }
};

const fmtDate = (ts) => {
  if (!ts) return "";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return "Today";
    const yes = new Date(now); yes.setDate(now.getDate()-1);
    if (d.toDateString() === yes.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-GB", { weekday:"long", day:"2-digit", month:"short" });
  } catch { return ""; }
};

const S = {
  card: { background:"#FFFFFF", border:"1px solid #E2DDD6", borderRadius:10 },
  inp:  { background:"#F4F2EE", border:"none", color:"#1A1A2E", padding:"10px 14px", borderRadius:24, fontSize:14, flex:1, outline:"none" },
};

export default function ChatCentre() {
  const [chats, setChats]           = useState([]);
  const [selectedChat, setSelected] = useState(null);
  const [messages, setMessages]     = useState([]);
  const [text, setText]             = useState("");
  const [sending, setSending]       = useState(false);
  const messagesEndRef               = useRef(null);

  const adminEmail = auth.currentUser?.email || "admin";
  const adminName  = adminEmail.split("@")[0];

  // Load all chat threads
  useEffect(() => {
    const q = query(collection(db, "chats"), orderBy("last_message_at", "desc"));
    return onSnapshot(q, snap => {
      setChats(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    });
  }, []);

  // Load messages for selected chat
  useEffect(() => {
    if (!selectedChat) return;
    const q = query(
      collection(db, "chats", selectedChat.id, "messages"),
      orderBy("sent_at", "asc"),
      limit(100)
    );
    return onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      // Mark officer messages as read
      snap.docs.forEach(d => {
        if (d.data().sender_role === "officer" && !d.data().read) {
          updateDoc(d.ref, { read: true });
        }
      });
      // Reset admin unread
      updateDoc(doc(db, "chats", selectedChat.id), { admin_unread: 0 }).catch(()=>{});
    });
  }, [selectedChat?.id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || !selectedChat || sending) return;
    setSending(true);
    const msg = text.trim();
    setText("");
    try {
      await addDoc(collection(db, "chats", selectedChat.id, "messages"), {
        text:        msg,
        sender_uid:  auth.currentUser?.uid || "admin",
        sender_name: adminName,
        sender_role: "admin",
        sent_at:     serverTimestamp(),
        read:        false,
      });
      await updateDoc(doc(db, "chats", selectedChat.id), {
        last_message:    msg,
        last_message_at: serverTimestamp(),
        last_sender:     "admin",
        officer_unread:  increment(1),
        admin_unread:    0,
      });

      // Also save as a notification so officer gets push
      await addDoc(collection(db, "notifications"), {
        type:        "broadcast",
        title:       "Message from Control Room",
        body:        msg,
        target_uid:  selectedChat.officer_uid,
        target_name: selectedChat.officer_name || selectedChat.officer_email,
        sent_by:     adminEmail,
        sent_at:     serverTimestamp(),
        read:        false,
        tenant_id:   "osecurity-ltd",
        chat_id:     selectedChat.id,
      });
    } catch(e) {
      console.error(e);
    }
    setSending(false);
  };

  const totalUnread = chats.reduce((sum, c) => sum + (c.admin_unread || 0), 0);

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const dateKey = fmtDate(msg.sent_at);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
    return groups;
  }, {});

  return (
    <div style={{ padding:"0 24px 24px", color:"#1A1A2E" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0 16px" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:"#1A1A2E" }}>
            Chat
            {totalUnread > 0 && (
              <span style={{ marginLeft:10, background:"#C0392B", color:"#fff", padding:"2px 10px", borderRadius:20, fontSize:12, fontWeight:700 }}>{totalUnread}</span>
            )}
          </div>
          <div style={{ fontSize:11, color:"#9B8F7A", marginTop:2 }}>
            {chats.length} conversations · Real-time messaging with officers
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"300px 1fr", gap:16, height:"calc(100vh - 200px)", minHeight:500 }}>

        {/* Conversations sidebar */}
        <div style={{ ...S.card, overflow:"hidden", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"12px 14px", borderBottom:"1px solid #E2DDD6", fontSize:11, fontWeight:700, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase" }}>
            Conversations
          </div>
          <div style={{ flex:1, overflowY:"auto" }}>
            {chats.length === 0 ? (
              <div style={{ padding:32, textAlign:"center", color:"#9B8F7A", fontSize:13 }}>
                No conversations yet.<br/>Officers will appear here once they message you.
              </div>
            ) : chats.map(chat => {
              const isSelected = selectedChat?.id === chat.id;
              const unread = chat.admin_unread || 0;
              const initials = (chat.officer_name || chat.officer_email || "?")[0].toUpperCase();
              return (
                <div key={chat.id} onClick={() => setSelected(chat)}
                  style={{
                    display:"flex", alignItems:"center", gap:10, padding:"12px 14px",
                    cursor:"pointer", borderBottom:"1px solid #F4F0E8",
                    background: isSelected ? "#FFF8E7" : "transparent",
                    borderLeft: isSelected ? "3px solid #C9A84C" : "3px solid transparent",
                  }}>
                  <div style={{ position:"relative", flexShrink:0 }}>
                    <div style={{ width:40, height:40, borderRadius:12, background: isSelected ? "#1A1A2E" : "#F0EDE6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800, color: isSelected ? "#C9A84C" : "#1A1A2E" }}>
                      {initials}
                    </div>
                    {chat.officer_online && (
                      <div style={{ position:"absolute", bottom:0, right:0, width:11, height:11, borderRadius:"50%", background:"#2E7D52", border:"2px solid #fff" }}/>
                    )}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ fontSize:13, fontWeight: unread>0 ? 800 : 600, color:"#1A1A2E", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {chat.officer_name || chat.officer_email || "Officer"}
                      </div>
                      <div style={{ fontSize:10, color:"#9B8F7A", flexShrink:0, marginLeft:6 }}>
                        {fmtTime(chat.last_message_at)}
                      </div>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:2 }}>
                      <div style={{ fontSize:12, color:"#9B8F7A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>
                        {chat.last_sender === "admin" ? "You: " : ""}{chat.last_message || "No messages yet"}
                      </div>
                      {unread > 0 && (
                        <span style={{ background:"#C0392B", color:"#fff", borderRadius:"50%", width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, flexShrink:0, marginLeft:6 }}>{unread}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat window */}
        {!selectedChat ? (
          <div style={{ ...S.card, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ textAlign:"center", color:"#9B8F7A" }}>
              <div style={{ width:72, height:72, background:"#F0EDE6", borderRadius:20, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
                <span style={{ fontSize:32 }}>💬</span>
              </div>
              <div style={{ fontSize:16, fontWeight:700, color:"#1A1A2E", marginBottom:8 }}>Select a conversation</div>
              <div style={{ fontSize:13 }}>Choose an officer from the sidebar to start messaging</div>
            </div>
          </div>
        ) : (
          <div style={{ ...S.card, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            {/* Chat header */}
            <div style={{ padding:"12px 16px", borderBottom:"1px solid #E2DDD6", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ position:"relative" }}>
                <div style={{ width:38, height:38, borderRadius:10, background:"#1A1A2E", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:"#C9A84C" }}>
                  {(selectedChat.officer_name || selectedChat.officer_email || "?")[0].toUpperCase()}
                </div>
                {selectedChat.officer_online && (
                  <div style={{ position:"absolute", bottom:0, right:0, width:10, height:10, borderRadius:"50%", background:"#2E7D52", border:"2px solid #fff" }}/>
                )}
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#1A1A2E" }}>{selectedChat.officer_name || selectedChat.officer_email}</div>
                <div style={{ fontSize:11, color: selectedChat.officer_online ? "#2E7D52" : "#9B8F7A" }}>
                  {selectedChat.officer_online ? "Online" : "Offline"}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:"auto", padding:"16px" }}>
              {Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  <div style={{ textAlign:"center", marginBottom:12 }}>
                    <span style={{ background:"#F0EDE6", color:"#9B8F7A", fontSize:11, fontWeight:600, padding:"3px 12px", borderRadius:20 }}>{date}</span>
                  </div>
                  {msgs.map((msg, i) => {
                    const isAdmin = msg.sender_role === "admin";
                    const prevMsg = msgs[i-1];
                    const showName = !isAdmin && (!prevMsg || prevMsg.sender_role !== msg.sender_role);
                    return (
                      <div key={msg.id} style={{ display:"flex", flexDirection:"column", alignItems: isAdmin ? "flex-end" : "flex-start", marginBottom:6 }}>
                        {showName && (
                          <div style={{ fontSize:11, color:"#9B8F7A", fontWeight:600, marginBottom:3, marginLeft:4 }}>
                            {msg.sender_name || "Officer"}
                          </div>
                        )}
                        <div style={{
                          maxWidth:"70%",
                          background: isAdmin ? "#1A1A2E" : "#FFFFFF",
                          color: isAdmin ? "#FFFFFF" : "#1A1A2E",
                          border: isAdmin ? "none" : "1px solid #E2DDD6",
                          borderRadius: isAdmin ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                          padding:"10px 14px",
                        }}>
                          <div style={{ fontSize:14, lineHeight:1.4 }}>{msg.text}</div>
                          <div style={{ display:"flex", alignItems:"center", gap:4, justifyContent:"flex-end", marginTop:4 }}>
                            <span style={{ fontSize:10, color: isAdmin ? "#9B8F7A" : "#9B8F7A" }}>{fmtTime(msg.sent_at)}</span>
                            {isAdmin && (
                              <span style={{ fontSize:12, color: msg.read ? "#C9A84C" : "#9B8F7A" }}>
                                {msg.read ? "✓✓" : "✓"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div ref={messagesEndRef}/>
            </div>

            {/* Input */}
            <div style={{ padding:"12px 16px", borderTop:"1px solid #E2DDD6", display:"flex", gap:10, alignItems:"flex-end" }}>
              <input
                style={S.inp}
                placeholder={`Message ${selectedChat.officer_name || selectedChat.officer_email || "officer"}...`}
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              />
              <button onClick={sendMessage} disabled={sending || !text.trim()} style={{
                width:44, height:44, borderRadius:"50%",
                background: text.trim() ? "#1A1A2E" : "#E2DDD6",
                border:"none", cursor: text.trim() ? "pointer" : "default",
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0
              }}>
                <span style={{ color: text.trim() ? "#C9A84C" : "#9B8F7A", fontSize:18 }}>➤</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
